import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, StatusChangeEvent, Validators } from '@angular/forms';
import { BillingTypes } from '@app/shared/constants';
import { HelpersService } from '@app/shared/services/helpers.service';
import { ProvidersService } from '@app/shared/services/providers.service';
import { filter, switchMap } from 'rxjs';
import {
  PROVIDER_SERVICES,
  ProviderServiceOption,
  RATING_TOOL_MAX_ROWS,
  ShippingProviders,
} from '@machool/common/constants';
import { RatingToolJobStatus, RatingToolService } from '@app/shared/services/rating-tool.service';
import { parse } from 'csv-parse/browser/esm/sync';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RatingToolProviderListItem } from './utils/rating-tool.interface';

@Component({
  standalone: false,
  selector: 'rating-tool',
  templateUrl: 'rating-tool.html',
  styleUrl: 'rating-tool.scss',
})
export class RatingToolComponent implements OnInit {
  private static readonly SUBMIT_ERROR =
    "There was an error when trying to submit the CSV. Please double check all the fields and ensure it's a valid CSV file.";

  private readonly destroyRef = inject(DestroyRef);
  private readonly helpersService = inject(HelpersService);
  private readonly providersService = inject(ProvidersService);
  private readonly ratingToolService = inject(RatingToolService);

  readonly ratingForm = new FormGroup({
    file: new FormControl<string | null>(null, Validators.required),
    courier: new FormGroup({
      courierEnum: new FormControl<ShippingProviders | null>(null, Validators.required),
      service: new FormControl<ProviderServiceOption | null>({ value: null, disabled: true }, Validators.required),
    }),
    margin: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    billingType: new FormControl<BillingTypes | null>(null, Validators.required),
    ignoreGlobalMargin: new FormControl<boolean>(false),
  });

  readonly billingTypes: BillingTypes[] = [
    BillingTypes.CREDIT_CARD,
    BillingTypes.MACHOOL_WALLET,
    BillingTypes.MACHOOL_ACCOUNT,
  ];

  readonly excludedProvidersList: ReadonlySet<string> = new Set<string>([
    ShippingProviders.CHRONO_EXPRESS,
    ShippingProviders.SHIFT,
    ShippingProviders.FEDEX,
    ShippingProviders.RIVO,
    ShippingProviders.INTELCOM,
    ShippingProviders.FLASHBOX,
    ShippingProviders.SPRING,
    ShippingProviders.LIVRAPIDE,
    ShippingProviders.ULALA,
    ShippingProviders.ZIPPLY,
    ShippingProviders.MULTILEG,
  ]);

  // Nationex T2 is a child MA account of Nationex with its own credentials, not a row returned by
  // getProvidersList (it has no espresso ShippingProviders member and no PROVIDER_SERVICES entry).
  // Appended here so the rating tool can offer it without touching the shared provider list.
  readonly nationexT2Courier: RatingToolProviderListItem = {
    enum: ShippingProviders.NATIONEXT2,
    name: 'Nationex T2',
    provider: ShippingProviders.NATIONEXT2,
    shipmentNotifications: 0,
    machoolAccount: 'available',
  };

  readonly providerList: RatingToolProviderListItem[] = [
    ...this.providersService.getProvidersList(),
    this.nationexT2Courier,
  ]
    .filter((provider) => !this.excludedProvidersList.has(provider.enum))
    .sort((a, b) => a.name.localeCompare(b.name));
  readonly jobStatus = signal<RatingToolJobStatus>({ state: 'waiting', progress: 0 });
  // The processor's return value, present only once the job has completed
  readonly jobResult = computed(() => (this.jobStatus().state === 'completed' ? this.jobStatus().result : undefined));
  readonly isFetching = signal<boolean>(false);
  // Set when the upload never became a job at all: the file failed to parse locally, or the POST itself
  // errored. Distinct from jobStatus 'failed', which means the job ran and the processor threw.
  readonly submitError = signal<string | null>(null);

  public serviceOptions: ProviderServiceOption[] = [];
  public csvFile: File | null = null;

  ngOnInit(): void {
    const courierGroup = this.ratingForm.controls.courier;
    const serviceControl = courierGroup.controls.service;

    courierGroup.controls.courierEnum.events.pipe(filter((e) => e instanceof StatusChangeEvent)).subscribe((e) => {
      if (e.status === 'VALID') {
        const courierCode = courierGroup.controls.courierEnum.value;
        this.serviceOptions = this.getServicesFromCourierCode(courierCode);
        serviceControl.reset();
        serviceControl.enable();
      }
    });
  }

  public billingTypeToString(billingType: BillingTypes): string {
    return this.helpersService.convertBillingTypesEnumToString(billingType);
  }

  public async fetchMachoolRates() {
    if (this.isFetching()) return;
    this.isFetching.set(true); // show progress bar
    this.jobStatus.set({ state: 'waiting', progress: 0 }); // clear the previous run's summary
    this.submitError.set(null);

    let csvObject: unknown[];
    try {
      const rawCsv = await this.csvFile.text();
      csvObject = parse(rawCsv, {
        columns: true,
        skip_empty_lines: true,
        bom: true,
      });
    } catch (err) {
      this.submitError.set(RatingToolComponent.SUBMIT_ERROR);
      this.isFetching.set(false);
      return;
    }

    // The backend rejects this with a 400 too (RatingToolService.getCourierRatesFromCsv); checked here so a
    // headers-only file never costs a round trip. Note @IsNotEmpty on the DTO does NOT catch an empty array.
    if (!csvObject.length) {
      this.submitError.set('This CSV has no rows. Add at least one shipment, or start from the sample CSV.');
      this.isFetching.set(false);
      return;
    }

    // Also a 400 from the backend; checked here so the admin sees the row count rather than the generic SUBMIT_ERROR
    if (csvObject.length > RATING_TOOL_MAX_ROWS) {
      this.submitError.set(
        `This CSV has ${csvObject.length} rows. The rating tool accepts a maximum of ${RATING_TOOL_MAX_ROWS} rows per upload — please split the file and submit it in batches.`,
      );
      this.isFetching.set(false);
      return;
    }

    const { file, ...dto } = this.ratingForm.getRawValue();

    const req = {
      file: csvObject,
      dto,
    };

    // Creates a job to process rates from CSV, and then polls for progress until
    // job is complete
    this.ratingToolService
      .fetchMachoolRates(req)
      .pipe(
        switchMap((res) => this.ratingToolService.pollJob(res.jobId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.jobStatus.set(res);
        },
        error: (err) => {
          console.error(err);
          this.submitError.set(RatingToolComponent.SUBMIT_ERROR);
          this.isFetching.set(false);
        },
        // pollJob's takeWhile completes the stream on every terminal state (including 'unknown'),
        // so this fires regardless of which one is reached — no need to enumerate them here too.
        complete: () => {
          this.isFetching.set(false);
        },
      });
  }

  public updateUploadedFile(file: File | undefined) {
    this.csvFile = file ?? null;
  }

  private getServicesFromCourierCode(code: ShippingProviders | string): ProviderServiceOption[] {
    if (!code) return [];
    // T2 has no PROVIDER_SERVICES entry of its own and Nationex does not let you pick a service
    // anyway (the adapter never sends service.code), so mirror Nationex's single option.
    const lookup = code === ShippingProviders.NATIONEXT2 ? ShippingProviders.NATIONEX : code;
    const courier = PROVIDER_SERVICES.find((provider) => provider.provider === lookup);
    return courier ? courier.servicesAvailable : [];
  }
}
